import {
  FRIENDLY_CONNECTIVITY_MESSAGE,
  isConnectivityErrorMessage,
  isConnectivityFailure,
} from '@buxus/shared'

/** Login-screen modal title for sign-in and profile failures. */
export const LOGIN_ERROR_MODAL_TITLE = "Couldn't sign in"

/** Shown on the login screen after sign-in is blocked (session is cleared before this appears). */
export const REGISTRATION_PROFILE_LOGIN_ERROR =
  "We couldn't complete sign-in for this account. Please try again later or contact help@buxus.io for assistance."

/**
 * ABP business exception code from get-registration-profile for seeded portal-only accounts
 * (see backend BuxusErrorCodes.PortalOnlyAccount / localization key PortalOnlyAccount).
 */
export const PORTAL_ONLY_ACCOUNT_ERROR_CODE = 'Buxus:PortalOnlyAccount'

/** Fallback if the API returns the portal-only code without a localized message. */
export const PORTAL_ONLY_ACCOUNT_LOGIN_ERROR =
  'This account is for the Buxus operator portal only and cannot be used to sign in to the Buxus app. Please use a registered app account, or contact help@buxus.io for assistance.'

/** One-shot message for LoginScreen after a blocked OIDC callback (`sessionStorage`). */
export const LOGIN_PROFILE_ERROR_STORAGE_KEY = 'buxus-web-login-profile-error-v1'

/** Next `beginOidcLogin` sends `prompt=login` so the IdP does not silently re-use SSO. */
export const LOGIN_FORCE_PROMPT_STORAGE_KEY = 'buxus-web-login-force-prompt-v1'

/** User-facing copy for login failures — no CORS, HTTP codes, or other dev jargon. */

function looksTechnical(message: string): boolean {
  const lower = message.toLowerCase()
  return (
    lower.includes('cors') ||
    isConnectivityErrorMessage(message) ||
    lower.includes('oidc') ||
    lower.includes('openid') ||
    lower.includes('json.parse') ||
    lower.includes('unexpected token') ||
    lower.startsWith('request failed (') ||
    /\b[45]\d{2}\b/.test(message)
  )
}

/**
 * Map login / sign-in redirect errors to short, actionable copy for the login screen.
 */
export function friendlyLoginError(err: unknown): string {
  if (isConnectivityFailure(err)) {
    return FRIENDLY_CONNECTIVITY_MESSAGE
  }

  if (err instanceof Error) {
    const msg = err.message.trim()
    if (isConnectivityErrorMessage(msg)) {
      return FRIENDLY_CONNECTIVITY_MESSAGE
    }
    if (msg && msg.length < 180 && !looksTechnical(msg)) {
      return msg.toLowerCase().includes('try again') ? msg : `${msg} Please try again.`
    }
  }

  return "We couldn't sign you in right now. Please try again in a moment. If this continues, contact help@buxus.io."
}
